import React, { useState, useCallback, useEffect } from 'react';
import PinDropIcon from '@material-ui/icons/PinDrop';
import AddIcon from '@material-ui/icons/Add';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { useDispatch } from 'react-redux';
import { RMSAutocomplete } from '../../../shared/components/form-controls';
import generalServices, { getAddressById } from '../../services/generalServices';
import { executeApiAction } from '../../utility/apiHelper';
import { isEmpty } from 'features/shared/utility/helper';
import { RMSIconButton } from '../../components/buttons/index';
import AddAddressDialog from '../../../user/components/common/AddAddressDialog';

const SearchAddressAutoComplete = ({
  name,
  label,
  placeholder,
  onSelectionChange,
  defaultAddressId = null,
  defaultAddressSelectedCallback = null,
  showAddIcon = true,
  onAddIconClick = () => {},
  showDetailsIcon = true,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  // Matches legacy's AddressLookup2.js exactly — the inline Add-address icon is hidden
  // (not disabled) unless the user has Addresses.Create.
  const addressPerms = getFullPermissions('globals', 'Addresses', 'any');
  const canCreateAddress = addressPerms.Create;
  // Beyond legacy (which always shows this icon unconditionally) — the Address Details icon
  // is now hidden entirely unless the user has at least Read or Edit on Addresses.
  const canViewAddressDetails = Boolean(addressPerms.Read || addressPerms.Edit);

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editAddressId, setEditAddressId] = useState(null);

  const searchAddresses = async (searchText) => {
    await executeApiAction({
      setLoading,
      action: () => generalServices.getAddressesBySearch(searchText),
      onSuccess: (response) => setAddresses(response),
      onError: (error) => {
        dispatch(notify(error.message, 'error'));
      },
    });
  };

  const handleDefaultAddress = useCallback(
    async (addressId) => {
      if (!addressId) return;

      await executeApiAction({
        setLoading,
        dispatch,
        action: () => getAddressById(addressId),
        onSuccess: (response) => {
          setAddresses([response]);
          defaultAddressSelectedCallback?.(response);
        },
      });
    },
    [dispatch, defaultAddressSelectedCallback]
  );

  useEffect(() => {
    if (!isEmpty(defaultAddressId)) {
      handleDefaultAddress(defaultAddressId);
    }
  }, [defaultAddressId, handleDefaultAddress]);

  const handleAddressUpdated = useCallback(
    (address) => {
      if (!address) return;

      setAddresses((prev) =>
        prev.map((existing) =>
          existing.ptsAddressID === address.ptsAddressID ? { ...existing, ...address } : existing
        )
      );

      onSelectionChange?.(address);
    },
    [onSelectionChange]
  );

  return (
    <>
      <RMSAutocomplete
        name={name}
        label={label ?? 'Address'}
        placeholder={placeholder}
        options={addresses}
        optionLabel="FullAddressText"
        optionValue="ptsAddressID"
        getOptionLabel={(option) => option?.FullAddressText || ''}
        onSearch={searchAddresses}
        loading={loading}
        disabled={disabled}
        onSelectionChange={onSelectionChange}
        leftContent={() =>
          showAddIcon &&
          canCreateAddress && (
            <RMSIconButton
              icon={<AddIcon fontSize="small" />}
              tooltip="Add Address"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onAddIconClick?.(event);
              }}
            />
          )
        }
        rightContent={({ value }) =>
          showDetailsIcon &&
          value &&
          canViewAddressDetails && (
            <RMSIconButton
              icon={<PinDropIcon />}
              tooltip="Address Details"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setEditAddressId(value.ptsAddressID);
              }}
            />
          )
        }
      />

      {editAddressId && (
        <AddAddressDialog
          open={Boolean(editAddressId)}
          addressId={editAddressId}
          onClose={() => setEditAddressId(null)}
          onAddressCreated={handleAddressUpdated}
        />
      )}
    </>
  );
};

export default SearchAddressAutoComplete;
